import React, { useState, useEffect, Fragment } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { GenericTableV2 } from '../../components/_EOS/GenericTable/GenericTableV2'
import { FilterComponent } from '../../components/_WMS/FilterComponent'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useApi } from '../../hooks/useApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { usePagination } from '../../hooks/usePagination'
import { Status } from '../../hooks/useFetchState'
import { TypePosition } from '../../components/TypePosition/types/TypePosition'
import { Filter } from '../../components/_WMS/FilterComponent/types'
import { getTypePositions } from './config/api'
import { filterOptions, defaultFilters } from './config/filterOptions'
import { getColumnsConfig } from './config/tableColumns'
import { NewTypePositionEdit } from './components/NewTypePositionEdit'
import { Edit as EditIcon } from '@mui/icons-material'
import { useModal } from '../../hooks/useModal'

interface NewTypePositionPageProps {}

export const NewTypePositionPage: React.FC<NewTypePositionPageProps> = () => {
  // ==========================================
  // HOOKS DE ESTADO
  // ==========================================

  const { state, callApi } = useApi<{ items: TypePosition[]; totalItems: number }>()
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>(defaultFilters)
  const pagination = usePagination(5)
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  // ==========================================
  // HOOKS DE MODALES
  // ==========================================
  const editModal = useModal<{ typePositionId: number }>('Editar Tipo Posición')

  // ==========================================
  // CONFIGURACIÓN
  // ==========================================
  const columnsConfig = getColumnsConfig()

  // ==========================================
  // FUNCIONES DE DATOS
  // ==========================================
  const requestData = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    const filtersToUse = filters !== undefined ? filters : selectedFilters
    const pageToUse = page !== undefined ? page : pagination.page
    const pageSizeToUse = pageSize !== undefined ? pageSize : pagination.pageSize

    callApi(getTypePositions(filtersToUse, pageToUse, pageSizeToUse)).catch((error) => {
      openSnackBar(`Error al cargar datos: ${error}`, false)
    })
  }

  // ==========================================
  // HANDLERS
  // ==========================================
  const handlePaginationChange = (page: number, pageSize: number) => {
    const targetPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, targetPage, pageSize)
    pagination.setPagination(targetPage, pageSize)
  }

  const handleSearch = (filters: Filter[]) => {
    setSelectedFilters(filters)
    pagination.reset()
    requestData(filters, 0, pagination.pageSize)
  }

  // ==========================================
  // EFFECTS
  // ==========================================
  useEffect(() => {
    requestData()
  }, [])

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          options={filterOptions}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={handleSearch}
          includeSearchOnClean={true}
        />

        <GenericTableV2<TypePosition>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='typePositionId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePaginationChange
          }}
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              icon: <EditIcon />,
              disabled: (row) => !row.isEditable,
              onClick: (row) => editModal.openModal('xs', { typePositionId: row.typePositionId })
            }
          ]}
        />
      </StandardPage>

      <NewTypePositionEdit
        isOpen={editModal.isOpen}
        typePositionId={editModal.extendedProps?.typePositionId || 0}
        onClose={editModal.closeModal}
        onSuccess={(msg) => {
          openSnackBar(msg, true)
          requestData()
        }}
        onError={(msg) => {
          openSnackBar(msg, false)
        }}
      />

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}

export default NewTypePositionPage
